import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Mail, ArrowLeft, ArrowRight, ShieldCheck, MailCheck, Clock } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";

export default function TrialRequest() {
  useSeo("Testzugang anfordern – LAIzyDocu", "Fordern Sie Ihren kostenlosen LAIzyDocu-Testzugang an: E-Mail bestätigen und nach Freischaltung loslegen.");
  const navigate = useNavigate();
  const [step, setStep] = useState("email"); // email | code | done
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);

  const requestCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/trial/request", { email: email.trim() });
      toast.success("Code gesendet. Bitte prüfen Sie Ihr Postfach.");
      setStep("code");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Anfrage fehlgeschlagen."); }
    finally { setLoading(false); }
  };

  const verifyCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/trial/verify", { email: email.trim(), code: code.trim() });
      setStep("done");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Verifizierung fehlgeschlagen."); }
    finally { setLoading(false); }
  };

  const resend = async () => {
    try { await api.post("/trial/resend", { email: email.trim() }); toast.success("Neuer Code gesendet."); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div className="min-h-screen bg-stone-50 grid place-items-center px-6 py-10">
      <div className="w-full max-w-md bg-white rounded-3xl border border-stone-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-8 md:p-10">
        <Link to="/" data-testid="trial-home" className="inline-flex items-center gap-2 mb-6">
          <div className="w-10 h-10 rounded-xl bg-emerald-900 text-white grid place-items-center"><ShieldCheck className="w-5 h-5" /></div>
          <span className="font-display text-lg font-semibold text-emerald-900">LA<span className="text-emerald-600">I</span>zyDoc</span>
        </Link>

        {step === "email" && (
          <form onSubmit={requestCode} data-testid="trial-email-step">
            <h1 className="font-display text-3xl font-semibold text-emerald-900">Testzugang anfordern</h1>
            <p className="text-stone-500 mt-2 mb-7">Hinterlegen Sie Ihre E-Mail-Adresse. Wir senden Ihnen einen Bestätigungscode und benachrichtigen Sie, sobald Ihr Zugang freigeschaltet ist.</p>
            <Label htmlFor="trial-email" className="text-base font-medium text-stone-800">E-Mail-Adresse</Label>
            <div className="relative mt-2">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
              <Input id="trial-email" data-testid="trial-email" type="email" required value={email}
                onChange={(e) => setEmail(e.target.value)} autoComplete="email"
                className="h-14 pl-12 pr-4 text-lg rounded-xl" placeholder="praxis@beispiel.de" />
            </div>
            <Button type="submit" data-testid="trial-request-submit" disabled={loading}
              className="w-full h-16 mt-7 text-lg rounded-xl bg-emerald-900 hover:bg-emerald-800 text-white">
              {loading ? "Wird gesendet…" : "Bestätigungscode senden"} <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <p className="mt-6 text-sm text-stone-500 text-center">
              Bereits ein Konto? <Link to="/login" data-testid="link-login" className="text-emerald-900 font-medium hover:underline">Anmelden</Link>
            </p>
          </form>
        )}

        {step === "code" && (
          <form onSubmit={verifyCode} data-testid="trial-code-step">
            <button type="button" onClick={() => setStep("email")} className="inline-flex items-center gap-1 text-sm text-stone-500 hover:text-stone-700 mb-4">
              <ArrowLeft className="w-4 h-4" /> Zurück
            </button>
            <h1 className="font-display text-3xl font-semibold text-emerald-900">Code eingeben</h1>
            <p className="text-stone-500 mt-2 mb-7">Wir haben einen 6-stelligen Code an <span className="font-medium text-stone-800">{email}</span> gesendet.</p>
            <Label htmlFor="trial-code" className="text-base font-medium text-stone-800">Verifizierungscode</Label>
            <Input id="trial-code" data-testid="trial-code" inputMode="numeric" pattern="[0-9]*" maxLength={6} required value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} autoComplete="one-time-code"
              className="h-16 mt-2 text-center text-3xl tracking-[0.5em] font-semibold rounded-xl" placeholder="000000" />
            <Button type="submit" data-testid="trial-verify-submit" disabled={loading || code.length !== 6}
              className="w-full h-16 mt-7 text-lg rounded-xl bg-emerald-900 hover:bg-emerald-800 text-white">
              {loading ? "Wird geprüft…" : "Bestätigen"}
            </Button>
            <button type="button" data-testid="trial-resend" onClick={resend} className="mt-5 w-full text-sm text-stone-500 hover:text-emerald-900">
              Keinen Code erhalten? Erneut senden
            </button>
          </form>
        )}

        {step === "done" && (
          <div data-testid="trial-done-step" className="text-center">
            <div className="w-16 h-16 rounded-2xl bg-emerald-50 text-emerald-700 grid place-items-center mx-auto mb-6"><MailCheck className="w-8 h-8" /></div>
            <h1 className="font-display text-3xl font-semibold text-emerald-900">E-Mail bestätigt</h1>
            <p className="text-stone-600 mt-3 leading-relaxed">
              Vielen Dank! Ihre Anfrage für <span className="font-medium text-stone-900">{email}</span> liegt uns nun vor.
            </p>
            <div className="mt-6 flex items-start gap-3 text-left bg-amber-50 border border-amber-100 rounded-2xl p-4">
              <Clock className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800">Sobald Ihr Testzugang <strong>freigeschaltet</strong> ist, erhalten Sie eine E-Mail mit einem Link, über den Sie Ihre Registrierung abschließen können.</p>
            </div>
            <Button onClick={() => navigate("/", { replace: true })} data-testid="trial-done-home"
              className="w-full h-14 mt-7 rounded-xl bg-emerald-900 hover:bg-emerald-800 text-white">Zur Startseite</Button>
          </div>
        )}
      </div>
    </div>
  );
}
